/**
 * FLOW EXECUTION HISTORY COMPONENT
 * Flow végrehajtási előzmények megjelenítése
 */

import React, { useState, useEffect } from 'react';
import { 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { getFlowExecutions } from '../../services/flowBuilderService';
import { useAuth } from '../../contexts/AuthContext';

interface FlowExecutionHistoryProps {
  flowId: string;
  onClose?: () => void;
}

interface ExecutionRecord {
  id: string;
  flowId: string;
  organizationId: string;
  userId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: Date;
  completedAt?: Date;
  initialData: any;
  result?: any;
  executionLog: ExecutionLogEntry[];
  currentNodeId?: string;
  variables: Record<string, any>;
  completedNodes: string[];
  failedNodes: string[];
}

interface ExecutionLogEntry {
  nodeId: string;
  nodeType: string;
  timestamp: Date;
  success: boolean;
  data?: any;
  message?: string;
  error?: string;
}

const FlowExecutionHistory: React.FC<FlowExecutionHistoryProps> = ({ 
  flowId, 
  onClose 
}) => {
  const { user } = useAuth();
  const [executions, setExecutions] = useState<ExecutionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<string | null>(null);
  const [expandedExecutions, setExpandedExecutions] = useState<Set<string>>(new Set());

  // Load execution history
  useEffect(() => {
    const loadExecutions = async () => {
      if (!user?.organizationId || !flowId) return;

      try {
        setLoading(true);
        const history = await getFlowExecutions(flowId, user.organizationId, 20);
        setExecutions(history);
      } catch (error) {
        console.error('Error loading execution history:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExecutions();
  }, [flowId, user?.organizationId]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <PlayIcon className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4 text-gray-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (start: Date, end?: Date) => {
    const endTime = end || new Date();
    const duration = endTime.getTime() - start.getTime();
    
    if (duration < 1000) return `${duration}ms`;
    if (duration < 60000) return `${Math.round(duration / 1000)}s`;
    if (duration < 3600000) return `${Math.round(duration / 60000)}m`;
    return `${Math.round(duration / 3600000)}h`;
  };

  const toggleExecutionExpand = (executionId: string) => {
    setExpandedExecutions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(executionId)) {
        newSet.delete(executionId);
      } else {
        newSet.add(executionId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Betöltés...</span>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Végrehajtási előzmények
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {executions.length} végrehajtás
        </p>
      </div>

      {/* Execution List */}
      <div className="flex-1 overflow-y-auto">
        {executions.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            <ClockIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Még nincs végrehajtási előzmény</p>
          </div>
        ) : (
          <div className="space-y-2 p-4">
            {executions.map((execution) => (
              <div
                key={execution.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Execution Summary */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={() => toggleExecutionExpand(execution.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(execution.status)}
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {execution.id.slice(-8)}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(execution.status)}`}>
                            {execution.status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {execution.startedAt ? execution.startedAt.toLocaleString('hu-HU') : 'Ismeretlen időpont'} • 
                          {execution.completedAt && execution.startedAt
                            ? ` ${formatDuration(execution.startedAt, execution.completedAt)}`
                            : execution.startedAt 
                              ? ` ${formatDuration(execution.startedAt)} (futó)`
                              : ' Időtartam ismeretlen'
                          }
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {(execution.completedNodes || []).length}/{(execution.completedNodes || []).length + (execution.failedNodes || []).length} node
                      </span>
                      {expandedExecutions.has(execution.id) ? (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Execution Details */}
                {expandedExecutions.has(execution.id) && (
                  <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    {/* Execution Log */}
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                        Végrehajtási napló
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {(execution.executionLog || []).length === 0 ? (
                          <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                            <p className="text-sm">Nincs végrehajtási napló</p>
                          </div>
                        ) : (
                          (execution.executionLog || []).map((logEntry, index) => (
                          <div
                            key={index}
                            className={`flex items-start space-x-3 p-2 rounded ${
                              logEntry.success 
                                ? 'bg-green-50 dark:bg-green-900/20' 
                                : 'bg-red-50 dark:bg-red-900/20'
                            }`}
                          >
                            {logEntry.success ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 text-red-500 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {logEntry.nodeId}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  ({logEntry.nodeType})
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {logEntry.timestamp ? 
                                    (logEntry.timestamp instanceof Date ? 
                                      logEntry.timestamp.toLocaleTimeString('hu-HU') : 
                                      new Date(logEntry.timestamp).toLocaleTimeString('hu-HU')
                                    ) : 'Ismeretlen idő'}
                                </span>
                              </div>
                              {logEntry.message && (
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                  {logEntry.message}
                                </p>
                              )}
                              {logEntry.error && (
                                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                  {logEntry.error}
                                </p>
                              )}
                            </div>
                          </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Variables */}
                    {Object.keys(execution.variables || {}).length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Változók
                        </h4>
                        <div className="bg-gray-100 dark:bg-gray-800 rounded p-3 text-sm font-mono">
                          <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                            {JSON.stringify(execution.variables || {}, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Result */}
                    {execution.result && (
                      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Eredmény
                        </h4>
                        <div className="bg-gray-100 dark:bg-gray-800 rounded p-3 text-sm font-mono">
                          <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                            {JSON.stringify(execution.result, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowExecutionHistory;
